import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Mail, Phone, MapPin, Instagram, Facebook } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_joywin-photo-deals/artifacts/7835b5me_new%20logoo.PNG";
const JMAJOR_LOGO = "https://customer-assets-eiarnc6j.emergentagent.net/job_c5db327b-4bf4-420f-890a-b7ce4db2a362/artifacts/jdkdmeym_Jmajor-Photoroom.png";

const Footer = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isDJPage = location.pathname.startsWith('/dj') || location.pathname === '/music';
  const currentLogo = isDJPage ? JMAJOR_LOGO : LOGO_URL;
  const logoAlt = isDJPage ? 'J MAJOR' : "Joywin's Clicks";
  const accent = isDJPage ? '#EF0000' : '#D4AF37';
  const [showAdminChoice, setShowAdminChoice] = useState(false);

  const hoverClass = isDJPage
    ? 'text-[#A1A1AA] hover:text-[#EF0000] transition-colors'
    : 'text-[#A1A1AA] hover:text-[#D4AF37] transition-colors';

  return (
    <footer
      className="border-t border-white/10"
      style={{ background: isDJPage ? 'linear-gradient(135deg, #0a0000, #1a0508, #0d0d0d)' : '#050505' }}
      data-testid="footer"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="md:col-span-2">
            <div className="mb-6">
              <img src={currentLogo} alt={logoAlt} className="h-20 object-contain" />
            </div>
            <p className="text-[#A1A1AA] mb-6 max-w-md">
              {isDJPage
                ? 'Professional DJ entertainment for every occasion. From intimate gatherings to grand celebrations, we bring the vibe.'
                : 'Professional photography services capturing your precious moments. From weddings to birthdays, we make every memory last forever.'}
            </p>
            <div className="flex gap-4">
              <a
                href={isDJPage ? "https://www.instagram.com/dj_jmajor/" : "https://www.instagram.com/joywins_clicks_and_music/"}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-[#1a1a1a] flex items-center justify-center text-[#A1A1AA] transition-all"
                style={{ '--hover-color': accent }}
                onMouseEnter={e => { e.currentTarget.style.color = accent; e.currentTarget.style.background = accent + '1a'; }}
                onMouseLeave={e => { e.currentTarget.style.color = '#A1A1AA'; e.currentTarget.style.background = '#1a1a1a'; }}
                data-testid="social-instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a
                href="https://facebook.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-[#1a1a1a] flex items-center justify-center text-[#A1A1AA] transition-all"
                onMouseEnter={e => { e.currentTarget.style.color = accent; e.currentTarget.style.background = accent + '1a'; }}
                onMouseLeave={e => { e.currentTarget.style.color = '#A1A1AA'; e.currentTarget.style.background = '#1a1a1a'; }}
                data-testid="social-facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-['Playfair_Display'] text-lg text-[#EDEDED] mb-6">Quick Links</h4>
            <ul className="space-y-3">
              <li><Link to="/" className={hoverClass}>Home</Link></li>
              <li><Link to={isDJPage ? "/dj" : "/packages"} className={hoverClass}>{isDJPage ? 'DJ Packages' : 'Packages'}</Link></li>
              <li><Link to="/reviews" className={hoverClass}>Reviews</Link></li>
              <li><Link to={isDJPage ? "/dj/book" : "/book"} className={hoverClass}>Book Now</Link></li>
              {!isDJPage && <li><Link to="/dj" className="text-[#A1A1AA] hover:text-[#EF0000] transition-colors font-medium">DJ</Link></li>}
              <li className="relative">
                <button
                  onClick={() => setShowAdminChoice(!showAdminChoice)}
                  className={`${hoverClass} text-sm`}
                  data-testid="footer-admin-btn"
                >
                  Admin
                </button>
                {showAdminChoice && (
                  <div className="absolute bottom-full left-0 mb-2 bg-[#111] border border-white/10 rounded-xl p-3 min-w-[180px] z-50 shadow-xl">
                    <p className="text-xs text-[#52525B] mb-2">Choose admin panel:</p>
                    <button
                      onClick={() => { navigate('/admin'); setShowAdminChoice(false); }}
                      className="block w-full text-left px-3 py-2 rounded-lg text-sm text-[#A1A1AA] hover:text-[#D4AF37] hover:bg-[#D4AF37]/10 transition-all"
                      data-testid="admin-choice-photo"
                    >
                      Joywin's Clicks
                    </button>
                    <button
                      onClick={() => { navigate('/dj/admin'); setShowAdminChoice(false); }}
                      className="block w-full text-left px-3 py-2 rounded-lg text-sm text-[#A1A1AA] hover:text-[#EF0000] hover:bg-[#EF0000]/10 transition-all"
                      data-testid="admin-choice-dj"
                    >
                      J MAJOR
                    </button>
                  </div>
                )}
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-['Playfair_Display'] text-lg text-[#EDEDED] mb-6">Contact Us</h4>
            <ul className="space-y-4">
              <li className="flex items-center gap-3">
                <Mail className="w-5 h-5" style={{ color: accent }} />
                <a href="mailto:joywinsclicksmusic@gmail.com" className={hoverClass}>
                  joywinsclicksmusic@gmail.com
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-5 h-5" style={{ color: accent }} />
                <a href="tel:+447404275825" className={hoverClass}>
                  +447404275825
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 flex-shrink-0" style={{ color: accent }} />
                <span className="text-[#A1A1AA]">United Kingdom</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom */}
        <div className="border-t border-white/10 mt-12 pt-8 text-center">
          <p className="text-[#52525B] text-sm">
            © {new Date().getFullYear()} {isDJPage ? 'J MAJOR' : "Joywin's Clicks"}. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
